import { WatchLaterTypes } from './types';
import api from '../../services/api';
import { normalizeVideoData } from '../../utils/dataHelpers';

// Toast notification function
const showToastNotification = (message, status = 'success') => {
  return {
    title: message,
    status: status,
    duration: 3000,
    isClosable: true,
    position: 'top',
  };
};

// Fetch user's watch later list
export const fetchWatchLater = () => async (dispatch) => {
  dispatch({ type: WatchLaterTypes.FETCH_WATCH_LATER_REQUEST });
  
  try {
    console.log('[WATCH-LATER-REDUX] Fetching user watch later');
    const response = await api.get('/api/user/watch-later');
    
    dispatch({
      type: WatchLaterTypes.FETCH_WATCH_LATER_SUCCESS,
      payload: response.data.watchLater
    });
    
    return response.data.watchLater;
  } catch (error) {
    console.error('[WATCH-LATER-ERROR] Error fetching watch later:', error);
    
    dispatch({
      type: WatchLaterTypes.FETCH_WATCH_LATER_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to fetch watch later'
    });
  }
};

// Add video to watch later
export const addToWatchLater = (videoData, timestamp = 0) => async (dispatch) => {
  dispatch({ type: WatchLaterTypes.ADD_TO_WATCH_LATER_REQUEST });
  
  try {
    console.log('[WATCH-LATER-REDUX] Adding to watch later:', videoData);
    console.log(`[WATCH-LATER-REDUX] Using timestamp: ${timestamp}`);
    
    // Ensure timestamp is included in the payload
    const payload = { 
      ...videoData, 
      timestamp: timestamp 
    };
    
    console.log('[WATCH-LATER-REDUX] Sending payload:', payload);
    
    // API call to add to watch later
    const response = await api.post('/api/user/watch-later/videos', payload);
    
    dispatch({
      type: WatchLaterTypes.ADD_TO_WATCH_LATER_SUCCESS,
      payload: response.data.watchLater
    });
    
    // Show success toast
    dispatch({
      type: WatchLaterTypes.SHOW_TOAST,
      payload: showToastNotification('Added to Watch Later', 'success')
    });
    
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[WATCH-LATER-ERROR] Error adding to watch later:', error);
    
    dispatch({
      type: WatchLaterTypes.ADD_TO_WATCH_LATER_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to add video to watch later'
    });
    
    // Show error toast
    dispatch({
      type: WatchLaterTypes.SHOW_TOAST,
      payload: showToastNotification('Failed to add to watch later', 'error')
    });
    
    throw error; // Re-throw to allow handling in the component
  }
};

// Update video timestamp in watch later
export const updateWatchLaterTimestamp = (videoId, timestamp) => async (dispatch) => {
  dispatch({
    type: WatchLaterTypes.UPDATE_TIMESTAMP_REQUEST,
    payload: { videoId, timestamp }
  });
  
  try {
    console.log(`[WATCH-LATER-REDUX] Updating timestamp for video ${videoId} to ${timestamp}s`);
    const response = await api.put(`/api/user/watch-later/videos/${videoId}/timestamp`, { timestamp });
    
    dispatch({
      type: WatchLaterTypes.UPDATE_TIMESTAMP_SUCCESS,
      payload: response.data.watchLater
    });
    
    // Show success toast
    dispatch({
      type: WatchLaterTypes.SHOW_TOAST,
      payload: showToastNotification('Progress saved', 'success')
    });
    
    return { success: true, watchLater: response.data.watchLater };
  } catch (error) {
    console.error('[WATCH-LATER-ERROR] Error updating timestamp:', error);
    
    dispatch({
      type: WatchLaterTypes.UPDATE_TIMESTAMP_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to update timestamp'
    });
    
    // Show error toast
    dispatch({
      type: WatchLaterTypes.SHOW_TOAST,
      payload: showToastNotification('Failed to save progress', 'error')
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Remove video from watch later
export const removeFromWatchLater = (videoId) => async (dispatch) => {
  dispatch({
    type: WatchLaterTypes.REMOVE_FROM_WATCH_LATER_REQUEST,
    payload: videoId
  });
  
  try {
    console.log(`[WATCH-LATER-REDUX] Removing video ${videoId} from watch later`);
    const response = await api.delete(`/api/user/watch-later/videos/${videoId}`);
    
    dispatch({
      type: WatchLaterTypes.REMOVE_FROM_WATCH_LATER_SUCCESS,
      payload: response.data.watchLater
    });
    
    // Show success toast
    dispatch({
      type: WatchLaterTypes.SHOW_TOAST,
      payload: showToastNotification('Removed from Watch Later', 'success')
    });
    
    return { success: true, watchLater: response.data.watchLater };
  } catch (error) {
    console.error('[WATCH-LATER-ERROR] Error removing from watch later:', error);
    
    dispatch({
      type: WatchLaterTypes.REMOVE_FROM_WATCH_LATER_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to remove video from watch later'
    });
    
    // Show error toast
    dispatch({
      type: WatchLaterTypes.SHOW_TOAST,
      payload: showToastNotification('Failed to remove from watch later', 'error')
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Reorder videos in watch later
export const reorderWatchLater = (videoPositions) => async (dispatch) => {
  dispatch({
    type: WatchLaterTypes.REORDER_WATCH_LATER_REQUEST,
    payload: videoPositions
  });
  
  try {
    console.log('[WATCH-LATER-REDUX] Reordering watch later');
    const response = await api.put('/api/user/watch-later/reorder', { videoPositions });
    
    dispatch({
      type: WatchLaterTypes.REORDER_WATCH_LATER_SUCCESS,
      payload: response.data.watchLater
    });
    
    return { success: true, watchLater: response.data.watchLater };
  } catch (error) {
    console.error('[WATCH-LATER-ERROR] Error reordering watch later:', error);
    
    dispatch({
      type: WatchLaterTypes.REORDER_WATCH_LATER_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to reorder watch later'
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Load watch later for playback
export const loadWatchLaterForPlayback = () => async (dispatch) => {
  dispatch({ type: WatchLaterTypes.LOAD_WATCH_LATER_FOR_PLAYBACK_REQUEST });
  
  try {
    console.log('[WATCH-LATER-PLAYER] Initializing watch later playback');
    console.time('watch-later-player-load');
    
    // Fetch watch later with full video data
    const response = await api.get('/api/user/watch-later');
    const { videos: rawVideos } = response.data.watchLater;
    
    // Normalize all videos to ensure consistent properties
    const videos = rawVideos.map(video => normalizeVideoData(video))
                           .sort((a, b) => a.position - b.position);
    
    console.timeEnd('watch-later-player-load');
    console.log(`[WATCH-LATER-PLAYER] Loaded watch later with ${videos.length} videos`);
    
    dispatch({ 
      type: WatchLaterTypes.LOAD_WATCH_LATER_FOR_PLAYBACK_SUCCESS, 
      payload: {
        videos,
        startIndex: 0
      }
    });
    
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[WATCH-LATER-PLAYER] Failed to load watch later for playback:', error);
    
    dispatch({ 
      type: WatchLaterTypes.LOAD_WATCH_LATER_FOR_PLAYBACK_FAILURE, 
      payload: error.response?.data?.message || 'Failed to load watch later for playback' 
    });
    
    return { success: false, error: error.message };
  }
};

// Set playback index
export const setWatchLaterPlaybackIndex = (index) => ({
  type: WatchLaterTypes.SET_WATCH_LATER_PLAYBACK_INDEX,
  payload: index
});

// Navigation controls for player
export const nextWatchLaterVideo = () => ({
  type: WatchLaterTypes.NEXT_WATCH_LATER_VIDEO
});

export const previousWatchLaterVideo = () => ({
  type: WatchLaterTypes.PREVIOUS_WATCH_LATER_VIDEO
});

// Toggle autoplay
export const toggleWatchLaterAutoplay = (enabled) => ({
  type: WatchLaterTypes.TOGGLE_WATCH_LATER_AUTOPLAY,
  payload: enabled
});

// Clear playback state
export const clearWatchLaterPlayback = () => ({
  type: WatchLaterTypes.CLEAR_WATCH_LATER_PLAYBACK
});
